import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight, Brain,
  CheckCircle2, Download, IndianRupee, LineChart as LineIcon,
  Loader2, ShieldAlert, Sparkles, TrendingDown, TrendingUp, Wallet,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { downloadNodeAsPdf } from "@/lib/screenToPdf";
import { OwnerKpi, MetalChip, SummaryStrip } from "@/components/owner-reports/OwnerUI";
import { HeartPulse, Percent, Receipt, ChevronDown, ChevronUp, LayoutGrid } from "lucide-react";

const TIER_TONE = { Excellent: "green", Stable: "green", Warning: "amber", Critical: "red", Dangerous: "red" };
const TONE_FILL = { green: "emerald", amber: "gold", red: "ruby", blue: "blue" };

function FounderPanel({ narrative, generatedAt }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-2xl border-[3px] border-[#d9ae52] bg-gradient-to-br from-[#fff4d1] to-[#f6e1a4] p-4" data-testid="founder-panel">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left" data-testid="founder-panel-toggle">
        <Brain className="w-6 h-6 text-[#750817]" />
        <span className="flex-1 text-xl font-bold text-[#750817]" style={{ fontFamily: "'Playfair Display', serif" }}>Founder&apos;s Monthly Reality Check</span>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && <>
        <p className="mt-2 text-base leading-relaxed text-[#3b1a12] whitespace-pre-wrap" data-testid="founder-panel-text">{narrative?.founder_summary}</p>
        <div className="text-xs mt-2 text-[#5a3a22]">AI-assisted · GPT-5.2 · Generated {generatedAt ? new Date(generatedAt).toLocaleString() : "—"}</div>
      </>}
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- helpers ----------
import { resolveCurrency, formatMoney } from "../utils/money";
const sym = (currency) => resolveCurrency(currency).symbol;
const fmt = (n, currency) => formatMoney(n, resolveCurrency(currency), { digits: 0 });
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const monthOptions = () => {
  // last 24 months ending current month
  const out = [];
  const d = new Date();
  for (let i = 0; i < 24; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const key = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
    const label = m.toLocaleString("en-US", { month: "short", year: "numeric" });
    out.push({ key, label });
  }
  return out;
};

const tierStyles = {
  Excellent: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  Stable:    { bg: "bg-green-50",   border: "border-green-200",   text: "text-green-700",   dot: "bg-green-500" },
  Warning:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   dot: "bg-amber-500" },
  Critical:  { bg: "bg-orange-50",  border: "border-orange-200",  text: "text-orange-700",  dot: "bg-orange-500" },
  Dangerous: { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-700",     dot: "bg-red-500" },
};

const severityBadge = {
  critical: "bg-red-100 text-red-800 border-red-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-slate-100 text-slate-700 border-slate-300",
};

// ---------- sub-components ----------
function HealthMeter({ score, currency }) {
  const tier = score?.tier || "Stable";
  const s = tierStyles[tier] || tierStyles.Stable;
  const val = Math.max(0, Math.min(100, score?.score ?? 0));
  return (
    <div className={`rounded-2xl border ${s.border} ${s.bg} p-6 flex flex-col items-center justify-center h-full`} data-testid="health-score-card">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Health Score</div>
      <div className={`text-6xl font-black mt-2 ${s.text}`} data-testid="health-score-value">{val.toFixed(0)}</div>
      <div className={`mt-1 inline-flex items-center gap-2 text-sm font-bold ${s.text}`} data-testid="health-score-tier">
        <span className={`w-2 h-2 rounded-full ${s.dot} animate-pulse`} />
        {tier}
      </div>
      <div className="mt-4 w-full bg-white/60 rounded-full h-2 overflow-hidden">
        <div className={`h-2 ${s.dot.replace("bg-", "bg-")}`} style={{ width: `${val}%` }} />
      </div>
      <div className="text-[10px] mt-2 text-slate-500 uppercase tracking-wide">0–100 scale · {currency}</div>
    </div>
  );
}

function FounderSummary({ narrative, generatedAt }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-6 h-full" data-testid="founder-summary">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-amber-700" />
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-800">Founder's Monthly Reality Check</span>
      </div>
      <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">{narrative?.founder_summary}</p>
      <div className="text-[10px] mt-3 text-slate-500">AI-assisted · GPT-5.2 · Generated {generatedAt ? new Date(generatedAt).toLocaleString() : "—"}</div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "slate", trend, "data-testid": testid }) {
  const tones = {
    slate: "bg-white border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    red: "bg-red-50 border-red-200",
    blue: "bg-blue-50 border-blue-200",
    amber: "bg-amber-50 border-amber-200",
    rose: "bg-rose-50 border-rose-200",
    indigo: "bg-indigo-50 border-indigo-200",
    fuchsia: "bg-fuchsia-50 border-fuchsia-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-600 font-medium">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      </div>
      <div className="text-xl font-bold text-slate-900 mt-2">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
      {trend != null && (
        <div className={`mt-2 flex items-center gap-1 text-xs ${trend >= 0 ? "text-emerald-700" : "text-red-700"}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend).toFixed(1)}% vs prev
        </div>
      )}
    </div>
  );
}

function RatioRow({ label, current, ideal, idealLabel = "≤" }) {
  let status, color, Icon;
  if (current <= ideal) {
    status = "OK"; color = "text-emerald-700 bg-emerald-50"; Icon = CheckCircle2;
  } else if (current <= ideal * 1.3) {
    status = "High"; color = "text-amber-700 bg-amber-50"; Icon = AlertTriangle;
  } else {
    status = "Critical"; color = "text-red-700 bg-red-50"; Icon = ShieldAlert;
  }
  return (
    <div className="grid grid-cols-12 items-center gap-2 py-2.5 border-b border-slate-100 last:border-0 text-sm">
      <div className="col-span-5 font-medium text-slate-700">{label}</div>
      <div className="col-span-3 font-bold text-slate-900">{pct(current)}</div>
      <div className="col-span-2 text-xs text-slate-500">{idealLabel} {pct(ideal)}</div>
      <div className={`col-span-2 inline-flex items-center justify-end gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${color}`}>
        <Icon className="w-3 h-3" /> {status}
      </div>
    </div>
  );
}

function TrendStrip({ trend, currency }) {
  if (!trend?.length) return null;
  const max = Math.max(...trend.map((t) => Math.abs(t.net_profit)), 1);
  return (
    <div className="grid grid-cols-12 gap-1 h-32 items-end" data-testid="trend-strip">
      {trend.map((t, idx) => {
        const h = Math.max(2, (Math.abs(t.net_profit) / max) * 100);
        const positive = t.net_profit >= 0;
        return (
          <div key={idx} className="flex flex-col items-center justify-end h-full">
            <div
              className={`w-full rounded-t-sm ${positive ? "bg-emerald-500" : "bg-red-500"} opacity-80 hover:opacity-100 transition-opacity`}
              style={{ height: `${h}%` }}
              title={`${t.label} · P/L ${fmt(t.net_profit, currency)} · Sales ${fmt(t.sales, currency)}`}
            />
            <div className="text-[9px] text-slate-500 mt-1 truncate w-full text-center">{t.label.slice(0, 3)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- main page ----------
export default function CenterHealth({ externalCenter, externalMonth, embedded = false, strip = false } = {}) {
  const { session } = useAuth();
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState(externalCenter || "");
  const [month, setMonth] = useState(() => {
    if (externalMonth) return externalMonth;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const captureRef = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const healthReq = useRef(0);

  const months = useMemo(monthOptions, []);

  // Sync from parent (Owner Reports) when embedded
  useEffect(() => {
    if (externalCenter !== undefined && externalCenter !== null && externalCenter !== center) {
      setCenter(externalCenter);
    }
  }, [externalCenter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalMonth && externalMonth !== month) {
      setMonth(externalMonth);
    }
  }, [externalMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // A non-staff user (franchise owner / center manager) only ever has
  // access to their own center — the /centers endpoint returns exactly
  // that list. In that case we hide the center dropdown and display
  // the center as a read-only badge instead.
  const isStaff = Boolean(
    session?.is_super_admin ||
      session?.is_admin ||
      (session?.roles || {}).accounts ||
      (session?.roles || {}).mis ||
      (session?.roles || {}).accounting
  );
  const singleCenterLock = !isStaff && centers.length === 1;

  // Fetch accessible centers on mount
  useEffect(() => {
    if (!session?.token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/health-dashboard/centers`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: session.token }),
        });
        if (!r.ok) throw new Error(await r.text());
        const j = await r.json();
        setCenters(j.centers || []);
        if (j.centers?.length && !center) setCenter(j.centers[0].code);
      } catch (e) {
        toast.error(`Failed to load centers: ${e.message || e}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  const loadHealth = async () => {
    if (!center) return;
    const reqId = ++healthReq.current;
    setLoading(true);
    setData(null);
    try {
      const r = await fetch(`${API}/api/health-dashboard/score`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, center, month }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `HTTP ${r.status}`);
      }
      const j = await r.json();
      if (reqId === healthReq.current) setData(j);
    } catch (e) {
      if (reqId === healthReq.current) toast.error(`${e.message || e}`);
    } finally {
      if (reqId === healthReq.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (center && month && session?.token) loadHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, month, session?.token]);

  const downloadPdf = async () => {
    if (!center || !data) return;
    setDownloading(true);
    const wasClosed = !captureRef.current;
    if (wasClosed) { setDetailsOpen(true); await new Promise((res) => setTimeout(res, 500)); }
    if (!captureRef.current) { setDownloading(false); return; }
    // Add a class so the PDF-only header/footer become visible during capture
    const node = captureRef.current;
    node.classList.add("pdf-capturing");
    try {
      const monthLabel = months.find((m) => m.key === month)?.label || month;
      const filename = `Center_Health_${center}_${monthLabel.replace(/\s+/g, "_")}.pdf`;
      await downloadNodeAsPdf(node, filename, { orientation: "landscape" });
    } catch (e) {
      toast.error(`PDF failed: ${e.message || e}`);
    } finally {
      node.classList.remove("pdf-capturing");
      if (wasClosed) setDetailsOpen(false);
      setDownloading(false);
    }
  };

  const cur = data?.current;
  const prev = data?.previous;
  const yoy = data?.yoy;
  const currency = data?.currency || "INR";

  const prevTrend = useMemo(() => {
    if (!cur || !prev || !prev.sales?.total) return null;
    return ((cur.sales.total - prev.sales.total) / prev.sales.total) * 100;
  }, [cur, prev]);
  const profitTrend = useMemo(() => {
    if (!cur || !prev) return null;
    if (Math.abs(prev.net_profit) < 1) return null;
    return ((cur.net_profit - prev.net_profit) / Math.abs(prev.net_profit)) * 100;
  }, [cur, prev]);

  const r = data?.ratios || {};
  const ideal = data?.ideal_ratios || {};

  const fmtT = (t) => (t == null ? "—" : `${t >= 0 ? "+" : "−"}${Math.abs(t).toFixed(1)}%`);
  const trendTone = (t) => (t == null ? "blue" : t >= 0 ? "green" : "red");
  const metrics = data && cur ? [
    { k: "hs", label: "Health Score", value: `${(data.score?.score ?? 0).toFixed(0)} / 100`, sub: data.score?.tier, tone: TIER_TONE[data.score?.tier] || "blue", Icon: HeartPulse },
    { k: "nm", label: "Net Margin", value: pct(r.net_margin_pct), sub: cur.net_profit < 0 ? "Loss" : cur.net_profit > 0 ? "Profit" : "Break-even", tone: cur.net_profit < 0 ? "red" : cur.net_profit > 0 ? "green" : "blue", Icon: Percent },
    { k: "er", label: "Expense Ratio", value: pct((cur.expenses_total / (cur.sales.total || 1)) * 100), sub: "Expenses as % of sales", tone: "blue", Icon: Receipt },
    { k: "sg", label: "Sales vs Prev.", value: fmtT(prevTrend), sub: "vs previous month", tone: trendTone(prevTrend), Icon: prevTrend != null && prevTrend < 0 ? TrendingDown : TrendingUp },
    { k: "pg", label: "P/L vs Prev.", value: fmtT(profitTrend), sub: cur.net_profit < 0 ? "Still a loss this month" : "vs previous month", tone: trendTone(profitTrend), Icon: LineIcon },
    { k: "wc", label: "Working Capital", value: pct(data.wc.wc_pct), sub: `Status: ${data.wc.status}`, tone: data.wc.wc_pct >= 50 ? "green" : data.wc.wc_pct >= 0 ? "amber" : "red", Icon: Wallet },
  ] : [];
  const showDetails = strip || detailsOpen;

  const body = (
    <div className={embedded ? "" : "p-6 max-w-7xl mx-auto"} data-testid="center-health-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        {!embedded ? (
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-rose-600" />
              Center Profitability Health Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Single-source AI-assisted health view · powered by canonical financial helpers
            </p>
          </div>
        ) : (
          <div className="text-sm text-slate-500">
            Using selected center · month from Owner Reports header
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!embedded && (singleCenterLock ? (
            <div
              className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm font-semibold"
              data-testid="center-locked-badge"
            >
              <Activity className="w-4 h-4" />
              {centers[0].code} · {centers[0].name}
            </div>
          ) : (
            <Select value={center} onValueChange={setCenter}>
              <SelectTrigger className="w-56" data-testid="center-selector">
                <SelectValue placeholder="Select center" />
              </SelectTrigger>
              <SelectContent>
                {centers.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`center-opt-${c.code}`}>
                    {c.code} · {c.name} {c.country !== "India" ? `(${c.country})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          {!embedded && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40" data-testid="month-selector">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            onClick={loadHealth}
            disabled={loading || !center}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="bg-rose-700 hover:bg-rose-800 text-white"
            data-testid="download-pdf-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Empty / loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Computing health metrics…
        </div>
      )}
      {!loading && !data && (
        <div className="text-center py-20 text-slate-500" data-testid="empty-state">
          Pick a center and month to see the health view.
        </div>
      )}

      {data && !strip && (
        <div className="grid grid-cols-2 md:grid-cols-3 min-[1500px]:grid-cols-6 gap-3 mb-4" data-testid="health-metal-blocks">
          {metrics.map((m, i) => <OwnerKpi key={m.k} i={i} fill={TONE_FILL[m.tone]} Icon={m.Icon} label={m.label} value={m.value} sub={m.sub} testid={`health-block-${m.k}`} />)}
        </div>
      )}
      {data && <div className="mb-4"><FounderPanel narrative={data.narrative} generatedAt={data.generated_at} /></div>}
      {data && !strip && (
        <button type="button" onClick={() => setDetailsOpen(!detailsOpen)} className="mb-4 flex w-full items-center gap-2 rounded-xl border-2 border-[#e2c27a] bg-[#fffdf8] px-4 py-3 text-left text-lg font-bold text-[#750817]" data-testid="health-details-toggle">
          <LayoutGrid className="w-5 h-5" /><span className="flex-1">Profitability Details (Grid)</span>
          <span className="text-sm font-semibold text-[#5a3a22]">{detailsOpen ? "Hide Details" : "View Details"}</span>{detailsOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      )}
      {data && showDetails && (
        <div ref={captureRef} className="pdf-capture-container" data-testid="dashboard-capture">
          {/* PDF-only header — visible only when generating PDF */}
          <div className="pdf-only-header">
            <div className="flex items-start justify-between border-b-2 border-rose-700 pb-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-700 to-rose-900 flex items-center justify-center text-white font-black text-xl shadow-md">
                  PB
                </div>
                <div>
                  <div className="text-xl font-black text-rose-800 leading-tight">Purnabramha</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">IntraPB Portal · Franchise Operations</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-base font-bold text-slate-900">{data.center_name} ({data.center})</div>
                <div className="text-sm text-slate-600">{months.find((m) => m.key === month)?.label || month}</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Generated {new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-rose-600" />
              <h2 className="text-lg font-bold text-slate-900">Center Profitability Health Dashboard</h2>
            </div>
          </div>

          {/* Hero: Score + Founder Summary */}
          <div className="pdf-only-grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <HealthMeter score={data.score} currency={currency} />
            <div className="md:col-span-2">
              <FounderSummary narrative={data.narrative} generatedAt={data.generated_at} />
            </div>
          </div>

          {/* KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="kpi-grid">
            <KpiCard icon={IndianRupee} label="Sales" tone="blue"
              value={fmt(cur.sales.total, currency)}
              sub={`${cur.sales.bills_count} entries · ${pct((cur.sales.dinein/(cur.sales.total||1))*100)} dine-in`}
              trend={prevTrend} data-testid="kpi-sales" />
            <KpiCard icon={TrendingDown} label="Commissions" tone="rose"
              value={fmt(cur.commission, currency)}
              sub={`${pct(r.commission_pct)} of sales`} data-testid="kpi-comm" />
            <KpiCard icon={ShieldAlert} label="GST" tone="fuchsia"
              value={fmt(cur.gst, currency)}
              sub={`${pct(r.gst_pct)} of sales`} data-testid="kpi-gst" />
            <KpiCard icon={LineIcon} label="⭐ Revenue Share Base" tone="sky"
              value={fmt(cur.net_revenue, currency)}
              sub="Sales − Comm − GST · payout base" data-testid="kpi-rev-share-base" />
            <KpiCard icon={TrendingDown} label="Expenses" tone="amber"
              value={fmt(cur.expenses_total, currency)}
              sub={`${pct((cur.expenses_total/(cur.sales.total||1))*100)} of sales`} data-testid="kpi-expenses" />
            <KpiCard icon={cur.net_profit >= 0 ? TrendingUp : TrendingDown}
              label="Profit / Loss" tone={cur.net_profit >= 0 ? "emerald" : "red"}
              value={fmt(cur.net_profit, currency)}
              sub={`${pct(r.net_margin_pct)} margin`}
              trend={profitTrend} data-testid="kpi-pl" />
          </div>

          {/* Profitability formula + WC */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="lg:col-span-2" data-testid="formula-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><LineIcon className="w-4 h-4" /> Profitability Formula</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                {[
                  ["Total Sales", cur.sales.total, "bold"],
                  ["Less: GST (inclusive carve)", -cur.gst],
                  ["Less: Platform Commission", -cur.commission],
                  ["= ⭐ Revenue Share Base", cur.net_revenue, "bold-sky"],
                  ["Less: Operating Expenses", -cur.expenses_total],
                  ["= Profit / (Loss)", cur.net_profit, cur.net_profit >= 0 ? "bold-green" : "bold-red"],
                ].map(([label, value, tone], i) => (
                  <div key={i} className={`flex justify-between py-2 border-b border-slate-100 last:border-0 ${tone?.includes("bold") ? "font-bold" : ""} ${tone === "bold-green" ? "text-emerald-700 text-base" : tone === "bold-red" ? "text-red-700 text-base" : tone === "bold-sky" ? "text-sky-700 text-base" : ""}`}>
                    <span>{label}</span>
                    <span>{fmt(value, currency)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="wc-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Wallet className="w-4 h-4" /> Working Capital Health</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Current WC</span><span className="font-bold">{fmt(data.wc.current_wc, currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Base WC</span><span>{fmt(data.wc.base_wc, currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">% of Base</span><span className={data.wc.wc_pct >= 50 ? "text-emerald-700 font-bold" : data.wc.wc_pct >= 0 ? "text-amber-700 font-bold" : "text-red-700 font-bold"}>{pct(data.wc.wc_pct)}</span></div>
                <div className="mt-3">
                  <div className="bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div className={`h-3 ${data.wc.wc_pct >= 50 ? "bg-emerald-500" : data.wc.wc_pct >= 0 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.max(0, Math.min(100, data.wc.wc_pct))}%` }} />
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-2">Status: <span className="font-semibold">{data.wc.status}</span></div>
              </CardContent>
            </Card>
          </div>

          {/* Ratios + trend */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="lg:col-span-2" data-testid="ratios-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Operational Ratios (% of Sales)</CardTitle>
              </CardHeader>
              <CardContent>
                <RatioRow label="Food Cost" current={r.food_cost_pct} ideal={ideal.food_cost_pct} />
                <RatioRow label="Salary" current={r.salary_pct} ideal={ideal.salary_pct} />
                <RatioRow label="Rent" current={r.rent_pct} ideal={ideal.rent_pct} />
                <RatioRow label="Utility (LPG/Electricity/Water)" current={r.utility_pct} ideal={ideal.utility_pct} />
                <RatioRow label="Commission Burden" current={r.commission_pct} ideal={ideal.commission_burden_pct} />
                <RatioRow label="Aggregator Share of Sales" current={r.aggregator_share_pct} ideal={ideal.aggregator_share_pct} />
              </CardContent>
            </Card>
            <Card data-testid="trend-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">12-Month P/L Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <TrendStrip trend={data.trend} currency={currency} />
                {yoy && (
                  <div className="mt-4 text-xs text-slate-600 border-t pt-3">
                    <div className="font-semibold text-slate-700 mb-1">Same month last year ({yoy.label})</div>
                    <div className="flex justify-between"><span>Sales</span><span>{fmt(yoy.sales.total, currency)}</span></div>
                    <div className="flex justify-between"><span>Net P/L</span><span className={yoy.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}>{fmt(yoy.net_profit, currency)}</span></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Leakages */}
          <Card className="mb-6" data-testid="leakages-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                Monthly Leakage Analysis
                <span className="ml-auto text-xs font-normal text-slate-500">{data.leakages.length} signal(s)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.leakages.length === 0 ? (
                <div className="text-sm text-emerald-700 py-4 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> No significant leakage detected this month.
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.leakages.map((lk, i) => (
                    <div key={i} className="py-3 flex items-start gap-3" data-testid={`leakage-${i}`}>
                      <Badge variant="outline" className={`uppercase text-[10px] font-bold ${severityBadge[lk.severity] || severityBadge.low}`}>
                        {lk.severity}
                      </Badge>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-slate-900">{lk.category}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{lk.detail}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs text-slate-500">Est. Impact</div>
                        <div className="font-bold text-sm text-rose-700">{fmt(lk.impact, currency)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions + Predictive */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card data-testid="actions-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  What Needs Correction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3 text-sm text-slate-700">
                  {data.actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`action-${i}`}>
                      <span className="bg-rose-100 text-rose-800 font-bold text-xs rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200" data-testid="predictive-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-rose-800">
                  <ShieldAlert className="w-4 h-4" />
                  Predictive 3-Month Warning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
                  {data.narrative.predictive_warning}
                </p>
                <div className="text-[10px] text-slate-500 mt-3">
                  AI-assisted projection from current pattern · for management discussion, not a financial commitment
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Comparisons */}
          {prev && (
            <Card className="mb-6" data-testid="comparison-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Smart Comparisons</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Vs Previous Month ({prev.label})</div>
                    {[["Sales", cur.sales.total, prev.sales.total],
                      ["Expenses", cur.expenses_total, prev.expenses_total],
                      ["Net P/L", cur.net_profit, prev.net_profit]].map(([lbl, c, p]) => {
                        const delta = p ? ((c - p) / Math.abs(p)) * 100 : 0;
                        return (
                          <div key={lbl} className="flex justify-between py-1">
                            <span className="text-slate-600">{lbl}</span>
                            <span className={delta >= 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                              {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Channel Mix</div>
                    <div className="flex justify-between py-1"><span>Dine-in</span><span className="font-semibold">{fmt(cur.sales.dinein, currency)}</span></div>
                    <div className="flex justify-between py-1"><span>Aggregator</span><span className="font-semibold">{fmt(cur.sales.aggregator, currency)}</span></div>
                    <div className="flex justify-between py-1 text-xs text-slate-500"><span>Swiggy / Zomato / DoorDash</span><span>{fmt(cur.sales.swiggy, currency)} / {fmt(cur.sales.zomato, currency)} / {fmt(cur.sales.doordash, currency)}</span></div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Expense Buckets</div>
                    {Object.entries(data.expense_buckets)
                      .filter(([, v]) => v > 0)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between py-1">
                          <span className="capitalize text-slate-600">{k.replace("_", " ")}</span>
                          <span className="font-semibold">{fmt(v, currency)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* PDF-only footer — visible only when generating PDF */}
          <div className="pdf-only-footer mt-8 pt-6 border-t-2 border-slate-300">
            <div className="grid grid-cols-3 gap-8 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Prepared by</div>
                <div className="font-semibold text-slate-900">{session?.managerName || "—"}</div>
                <div className="text-xs text-slate-500 mt-0.5">{session?.is_super_admin ? "Super Admin" : session?.is_admin ? "Admin" : "Owner / Manager"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Date &amp; Time of Download</div>
                <div className="font-semibold text-slate-900">
                  {new Date().toLocaleString("en-IN", { dateStyle: "full", timeStyle: "short" })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Account Manager Signature</div>
                <div className="h-10 border-b-2 border-slate-400"></div>
                <div className="text-[10px] text-slate-500 mt-1">Signature &amp; Date</div>
              </div>
            </div>
            <div className="text-center text-[10px] text-slate-400 mt-6">
              Purnabramha IntraPB · Confidential financial summary · For internal franchise governance only
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (!strip) return body;
  return (
    <SummaryStrip Icon={HeartPulse} title="Profitability Health" desc="Centre performance, margins and key profit indicators." basis={`Basis: ${month}`}
      chips={metrics.length ? metrics.map((m) => <MetalChip key={m.k} label={m.label} value={m.value} sub={m.sub} tone={m.tone} testid={`or-health-chip-${m.k}`} />)
        : <div className="col-span-full text-base text-[#5a3a22]" data-testid="or-health-chip-empty">{loading ? "Computing health metrics…" : "No health data for this period."}</div>}
      open={detailsOpen} onToggle={() => setDetailsOpen(!detailsOpen)} detailsLabel="Profitability Details (Grid)" testid="or-health-strip">
      {body}
    </SummaryStrip>
  );
}
